import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { Menu, Activity, Search, MapPin, ChevronRight, ChevronDown, List, LocateFixed } from 'lucide-react'
import { casesAPI, doctorsAPI, favoritesAPI, providersAPI } from '../../services/api'
import { useProfileStore } from '../../stores'
import { getZipDistance, getZipCoords } from '../../utils/zipDistance'
import { isDoctorInNetwork, getInsuranceMatchType, patientHasInsurance as hasInsuranceFn } from '../../utils/insuranceMatch'
import { loadGoogleMaps, haversineMiles } from '../../utils/googleMaps'
import DoctorsMap from './DoctorsMap'
// ★ s105 (TestFlight b71, Dr. Rami: "Need to update this with the new map search modal with all NPPES
//   data"): the Doctors tab shows the national directory beside the Orella doctors, as the share picker
//   does - the same section, the same pins, the same tapped-pin card (with Call / Directions, no case).
import DirectoryNearby from '../../components/share/DirectoryNearby'
import MapPinCard from '../../components/share/MapPinCard'
import { dirMapPins } from '../../utils/dirMapPins'
import { SPECIALTY_TAXONOMY, PRIMARY_CARE_TAXONOMY, isPrimaryCareName } from '../../utils/specialtyChoices'
import { nearestZip5, zip5Point, devicePointIfAllowed } from '../../utils/zip5Locate'
import { doctorDistanceMiles } from '../../utils/locationDistance'
import { useMapArea } from '../../hooks/useMapArea' // s106: the share step's "Search this area"
import { milesOrigin } from '../../utils/mapMyLocation' // PROD-1184 / PROD-1187
import MapAreaButtons from '../../components/doctors/MapAreaButtons'
import StartCaseSheet from './StartCaseSheet' // s106: "Send my case to this office"
import OfficePickerSheet from '../../components/share/OfficePickerSheet' // s110: which office gets the case
import { otherOffices } from '../../utils/officeAddress' // s110
import orellaWordmarkWhite from '../../assets/logos/orella-wordmark-white-transparent.png'
import HamburgerMenu from '../../components/HamburgerMenu'
import NotificationBellButton from '../../components/NotificationBellButton'
import LanguageSelector, { LanguageButton } from '../../components/LanguageSelector'
import { useI18n } from '../../i18n'
import { formatProviderSpecialty } from '../../utils/specialtyResolver'
import { hasRealHours } from '../../utils/shareDoctorPanel'
import {
  SPECIALTY_FILTERS, DISTANCE_OPTIONS, formatNextSlot, FilterGroup, FilterOpt, DoctorCard,
  DoctorFilterChips, DoctorFiltersSheet,
} from '../../components/doctors/DoctorSearchKit'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner
import { DoctorsSearchCard, MyDoctorsStrip, ResultsHeader, MapLegend, MapExpandButton, NoOrellaYet, PcpRow, PracticeCards } from '../../components/doctors/DoctorsHomeKit' // s123: boards A–D, F
import PracticeSendSheet from '../../components/doctors/PracticeSendSheet' // s123 board F
import { groupOffices, practicesFromRows } from '../../utils/officeGroups' // s123 board F
import { officeCount } from '../../utils/doctorsHome' // s124: offices, never the 250 cap

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY || 'AIzaSyCJNsyeMsI4oYPOri_LV1s5JXurYpFzBu4'

/* ===== Next-slot helpers (v230.7.0) ===== */
function slotMillis(slot) {
  if (!slot) return Infinity
  const date = slot.date || slot.start || ''
  const time = slot.time || (Array.isArray(slot.times) ? slot.times[0] : '') || '00:00'
  const dt = new Date(`${date}T${time}`)
  return isNaN(dt.getTime()) ? Infinity : dt.getTime()
}


/* ===== Main Screen ===== */
export default function DoctorsScreen() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const insurance = useProfileStore((s) => s.profile?.insurance)
  const patientZip = useProfileStore((s) => s.profile?.identification?.zipCode || '')
  
  const [myDoctors, setMyDoctors] = useState([])
  const [allDoctors, setAllDoctors] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  // v212.2: Independent filter states — filters stack, not mutually exclusive
  const [specialtyFilter, setSpecialtyFilter] = useState(null) // null or specialty string
  const [insuranceActive, setInsuranceActive] = useState(false)
  const [virtualActive, setVirtualActive] = useState(false)
  const [availableActive, setAvailableActive] = useState(false)
  // ★ s106 (TestFlight b72, Dr. Rami: "default at 5mi so we are not pulling the entire universe"):
  //   the tab opens at 5 mi (the map fitted to it); Reset goes back to 5; "Any" stays in the chip.
  const DEFAULT_MILES = 5
  const [distanceFilter, setDistanceFilter] = useState(DEFAULT_MILES)
  const [showFilters, setShowFilters] = useState(false)
  const [sortBy, setSortBy] = useState('soonest') // 'soonest' | 'distance' | 'rating'
  const [favoriteIds, setFavoriteIds] = useState([])
  // v230.11.0 — map view (gated behind the browser key)
  const [viewMode, setViewMode] = useState('map') // 'list' | 'map' — map-first (v242.5.11)
  const [userLocation, setUserLocation] = useState(null) // { lat, lng, label }
  const [showLoc, setShowLoc] = useState(false)
  const [selectedMapDoc, setSelectedMapDoc] = useState(null)
  // s105: the directory half (doctors not on Orella) and its tapped pin.
  const [dirRows, setDirRows] = useState([])
  const [dirLoading, setDirLoading] = useState(false) // s114 B2: the directory search is in flight (the map's spinner)
  const [selectedDirNpi, setSelectedDirNpi] = useState(null)
  // s106: the directory office the patient chose to send a case to (StartCaseSheet `office`).
  const [startOffice, setStartOffice] = useState(null)
  const [officePick, setOfficePick] = useState(null) // s110: a doctor with several offices picks one first
  // s123 (board B): the map opened full screen (Expand); a portal, so PageWrapper's transform cannot misplace it.
  const [mapFull, setMapFull] = useState(false)
  // s123 (board A): "On Orella" chip — only clinicians on Orella (the other offices' list and pins hidden).
  const [orellaOnly, setOrellaOnly] = useState(false)
  // s123 (board F): the API's near search answered (practice names are searched too), and the practice / office sheet.
  const [nearLive, setNearLive] = useState(false)
  const [practiceSheet, setPracticeSheet] = useState(null)
  // s106 (b72: "Zoom in, search the area" / "They need to match"): the share step's own hook.
  const mapArea = useMapArea()
  const { area, setAreaView, clearArea } = mapArea
  const [dirZip, setDirZip] = useState('')
  // v242.5.11 — special navy header (hamburger drawer + language + notifications) + collapsible search
  const [menuOpen, setMenuOpen] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)
  const locInputRef = useRef(null)
  const searchInputRef = useRef(null) // s111: the always-shown name search
  const scrollTopRef = useRef(null)
  const mapBoxRef = useRef(null) // s105: a picked directory row brings the map (and its card) into view

  // v242.11.0 — Doctors opens at the top. viewMode defaults to 'map', and the
  // Google Map grabs focus on init, scrolling the header off-screen. Reset the
  // scroll container (and any scrolled ancestor) on mount, and once more after
  // the map settles.
  useEffect(() => {
    const reset = () => {
      const el = scrollTopRef.current
      if (el) el.scrollTop = 0
      let p = el && el.parentElement
      while (p) { if (p.scrollTop) p.scrollTop = 0; p = p.parentElement }
    }
    reset()
    const id = setTimeout(reset, 300)
    return () => clearTimeout(id)
  }, [])

  // Patient has usable insurance data when they've saved a carrier (not self-pay)
  const patientCarrierId = insurance?.providerId || ''
  const patientCarrierName = (insurance?.provider || '').toLowerCase()
  const patientIpaId = insurance?.ipaId || ''
  const patientHasInsurance = !insurance?.selfPay && (patientCarrierId || patientCarrierName)

  // Check if a doctor accepts the patient's insurance
  const doctorAcceptsInsurance = (doc) => isDoctorInNetwork(doc, insurance)

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      try {
        const [casesResult, availableResult, favs] = await Promise.all([
          casesAPI.list().catch(() => []),
          doctorsAPI.list().catch(() => []),
          favoritesAPI.getDoctors().catch(() => [])
        ])
        
        const caseList = Array.isArray(casesResult) ? casesResult : (casesResult?.cases || [])
        const myDoctorIds = [...new Set(caseList.map(c => c.doctor_id).filter(Boolean))]
        
        const available = Array.isArray(availableResult) ? availableResult : []
        // Compute distance from patient zip to each doctor's practice zip
        const withDistance = available.map(d => {
          const dist = patientZip && d.practice_zip ? getZipDistance(patientZip, d.practice_zip) : null
          return dist != null ? { ...d, _distance: dist } : d
        })
        setAllDoctors(withDistance)
        
        const myDocs = withDistance.filter(d => myDoctorIds.includes(d.id))
        const missingIds = myDoctorIds.filter(id => !myDocs.find(d => d.id === id))
        if (missingIds.length > 0) {
          const fetched = await Promise.all(
            missingIds.map(id => doctorsAPI.get(id).catch(() => null))
          )
          myDocs.push(...fetched.filter(Boolean))
        }
        
        setMyDoctors(myDocs)
        setFavoriteIds(favs || [])
      } catch (err) {
        console.error('[Doctors] Load error:', err)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [patientZip])

  // v230.7.0: Lazily fetch the next available slot for availability-enabled
  // doctors so cards can show "Next available" and the list can sort by soonest.
  // Pilot scale (handful of practices); paginates in batches and self-terminates
  // once every enabled doctor has a resolved (or null) _nextSlot.
  useEffect(() => {
    const pending = allDoctors.filter(d => d.availability_enabled === true && d._nextSlot === undefined)
    if (!pending.length) return
    let cancelled = false
    ;(async () => {
      const batch = pending.slice(0, 30)
      const results = await Promise.all(batch.map(async (d) => {
        try {
          const r = await doctorsAPI.getAvailableSlots(d.id, 14)
          const slot = (r?.enabled && r?.slots?.length) ? r.slots[0] : null
          return [d.id, slot]
        } catch { return [d.id, null] }
      }))
      if (cancelled) return
      const map = Object.fromEntries(results)
      const stamp = (list) => list.map(d => (d.id in map ? { ...d, _nextSlot: map[d.id] || null } : d))
      setAllDoctors(prev => stamp(prev))
      setMyDoctors(prev => stamp(prev))
    })()
    return () => { cancelled = true }
  }, [allDoctors])

  // Filter doctors — v212.2: independent stackable filters
  // v230.11.0 — default map location = patient's coarse zip centroid.
  // ★ s111 (TestFlight b75, Dr. Rami: "Map needs to open centered on where the user is or zip code of record"): the
  //   ZIP3 point is only the first frame; it is replaced by the ZIP's own centre, and by the device when location
  //   is already allowed. Neither replaces a place the patient chose (only a `home` point is replaced).
  useEffect(() => {
    if (!MAPS_KEY || userLocation) return
    let alive = true
    const label = patientZip ? `${t('doctors.near', { defaultValue: 'Near' })} ${patientZip}` : t('doctors.yourArea', { defaultValue: 'Your area' })
    const c = getZipCoords(patientZip)
    if (c) setUserLocation({ lat: c[0], lng: c[1], label, home: true })
    zip5Point(patientZip, providersAPI.zipAreas).then((p) => {
      if (alive && p) setUserLocation((u) => (!u || u.home ? { lat: p.lat, lng: p.lng, label, home: true } : u))
    })
    devicePointIfAllowed().then((p) => {
      if (alive && p) setUserLocation((u) => (!u || u.home ? { lat: p.lat, lng: p.lng, label: t('doctors.currentLocation', { defaultValue: 'Current location' }), precise: true } : u))
    })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientZip])

  // Places Autocomplete on the location-search input (only while the sheet is open).
  useEffect(() => {
    if (!showLoc || !MAPS_KEY) return
    let ac = null
    loadGoogleMaps(MAPS_KEY).then((maps) => {
      if (!locInputRef.current || !maps.places) return
      ac = new maps.places.Autocomplete(locInputRef.current, { fields: ['geometry', 'name', 'formatted_address'], types: ['geocode'] })
      ac.addListener('place_changed', () => {
        const place = ac.getPlace()
        const loc = place && place.geometry && place.geometry.location
        if (loc) {
          setUserLocation({ lat: loc.lat(), lng: loc.lng(), label: place.formatted_address || place.name || t('doctors.selectedLocation', { defaultValue: 'Selected location' }), precise: true })
          setShowLoc(false)
        }
      })
    }).catch(() => {})
    return () => { ac = null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLoc])

  // s105: the directory is searched by 5-digit ZIP. A precise location (device / searched address) is
  //   turned into its nearest ZIP ON THE PHONE (utils/zip5Locate, the share picker's own rule); the
  //   home ZIP otherwise.
  useEffect(() => {
    let alive = true
    if (userLocation && userLocation.precise) {
      nearestZip5(userLocation.lat, userLocation.lng, providersAPI.zipAreas)
        .then((z) => { if (alive) setDirZip((z && z.zip) || patientZip || '') })
        .catch(() => { if (alive) setDirZip(patientZip || '') })
    } else {
      setDirZip(patientZip || '')
    }
    return () => { alive = false }
  }, [userLocation, patientZip])
  // A new location from the bar starts from that location again.
  useEffect(() => { clearArea() }, [userLocation]) // eslint-disable-line react-hooks/exhaustive-deps
  const dirTaxonomy = useMemo(() => {
    if (!specialtyFilter) return []
    if (isPrimaryCareName(specialtyFilter)) return PRIMARY_CARE_TAXONOMY
    return SPECIALTY_TAXONOMY[specialtyFilter] || []
  }, [specialtyFilter])
  const dirSelected = selectedDirNpi ? dirRows.find((r) => r.npi === selectedDirNpi) : null
  // s110: the office picker searches from the same place as the lists (S2 counts the two list literals).
  const pickerZip = area && area.zip ? area.zip : dirZip
  // ★ PROD-1187 (Alex 2026-10-19, "a"): after "Search this area" the miles were measured from the
  //   searched area's centre. Every distance label is measured from the patient when their point is
  //   known (device / searched address); with a ZIP only, from the searched area or the ZIP, as before.
  //   The rows' miles are worked out on the phone (withOriginMiles); the API is unchanged.
  const distanceOrigin = milesOrigin(userLocation, area)
  const pickerOrigin = distanceOrigin

  const useDeviceLocation = () => {
    if (!navigator.geolocation) { setShowLoc(false); return }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: t('doctors.currentLocation', { defaultValue: 'Current location' }), precise: true }); setShowLoc(false) },
      () => setShowLoc(false),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    )
  }
  const useHomeZip = () => {
    const label = patientZip ? `${t('doctors.near', { defaultValue: 'Near' })} ${patientZip}` : t('doctors.yourArea', { defaultValue: 'Your area' })
    const c = getZipCoords(patientZip)
    if (c) setUserLocation({ lat: c[0], lng: c[1], label, home: true })
    // s111: then the ZIP's own centre (not the ZIP3 one).
    zip5Point(patientZip, providersAPI.zipAreas).then((p) => {
      if (p) setUserLocation((u) => (u && u.home ? { lat: p.lat, lng: p.lng, label, home: true } : u))
    })
    setShowLoc(false)
  }

  // Accurate distance from the chosen location to each MD's geocoded coords,
  // overriding the coarse zip-centroid distance when both are available.
  const allDoctorsLive = useMemo(() => {
    if (!MAPS_KEY || !userLocation) return allDoctors
    return allDoctors.map(d => {
      if (d.practice_lat != null && d.practice_lng != null) {
        const mi = haversineMiles(userLocation.lat, userLocation.lng, Number(d.practice_lat), Number(d.practice_lng))
        return { ...d, _distance: Math.round(mi * 10) / 10 }
      }
      return d
    })
  }, [allDoctors, userLocation])

  const filteredDoctors = useMemo(() => {
    // ★★ D-g (P1 round 3, H2): a physician without real hours is not listed. From here
    //   profile > "Start a case" runs the whole case-creation flow into a date-time step
    //   with nothing to pick and no send. HIDDEN, not badged: the smaller change, and
    //   the same hasRealHours the share picker uses, so there is one rule. This list
    //   feeds the map, the "Orella doctors" list and the result count.
    // ★★ s114 B4 (Dr. Rami 2026-10-22 build-80 bugs: "List them: 'No online booking yet'"; reverses D-g HERE).
    //   This tab is a directory: a physician without real hours is LISTED, flagged _noOnlineBooking for its badge;
    // ★★ D527 (Dr. Rami 2026-10-23, time request; handoff s116): he is now selectable everywhere a pick leads to a
    //   send; his profile shows the normal "Start a case" and the patient REQUESTS a time (badge: "Request a time").
    let docs = allDoctorsLive.map((d) => (hasRealHours(d) ? d : { ...d, _noOnlineBooking: true }))
    
    // Text search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      docs = docs.filter(d => 
        `${d.first_name} ${d.last_name}`.toLowerCase().includes(q) ||
        (d.specialties || []).some(s => (s || '').toLowerCase().includes(q)) ||
        (d.practice_name || '').toLowerCase().includes(q) ||
        (d.practice_city || '').toLowerCase().includes(q) ||
        (d.languages_spoken || []).some(l => l.toLowerCase().includes(q))
      )
    }
    
    // Specialty filter — v212.2: checks ALL entries in specialties array
    if (specialtyFilter) {
      const norm = (s) => (s || '').toString().toLowerCase().replace(/[()]/g, '').replace(/\s+/g, ' ').trim()
      const ENT_ALIASES = ['otolaryngology', 'otolaryngology ent', 'ent']
      const target = norm(specialtyFilter)
      const targetIsENT = ENT_ALIASES.includes(target)
      docs = docs.filter(d => {
        const specs = d.specialties || []
        return specs.some(s => {
          const docSpec = norm(s)
          if (!docSpec) return false
          if (docSpec === target) return true
          if (targetIsENT && ENT_ALIASES.includes(docSpec)) return true
          return docSpec.includes(target) || target.includes(docSpec)
        })
      })
    }
    
    // Insurance filter
    if (insuranceActive) {
      docs = docs.filter(d => doctorAcceptsInsurance(d))
    }

    // Virtual filter
    if (virtualActive) {
      docs = docs.filter(d => {
        const types = d.appointment_types || []
        const config = d.appointmentTypes || {}
        return types.some(t => t?.includes?.('virtual')) || config.newVirtual || config.followupVirtual
      })
    }

    // Scheduling filter
    if (availableActive) {
      docs = docs.filter(d => d.availability_enabled === true)
    }

    // Distance filter (narrow only — sorting handled below). s106: while an area is searched, the
    //   distance is measured from its centre (as on the share step). ★ s120 (Dr. Rami): the CHIP owns
    //   the radius; "Search this area" only moves the centre.
    if (distanceFilter && !area) {
      // s114 B4: a physician whose office has no map point (no distance) is kept, listed after the ones with a distance
      //   (the sorts use _distance ?? 9999); before, the default distance silently dropped him.
      docs = docs.filter(d => d._distance == null || d._distance <= distanceFilter)
    }
    if (area) {
      docs = docs.filter(d => {
        const dist = doctorDistanceMiles(d, { coords: { lat: area.lat, lng: area.lng }, zip: area.zip })
        return dist != null && dist <= (distanceFilter || 25)
      })
    }

    // v230.8.0: Sort respects the chosen sortBy. Default 'soonest' leads with
    // next availability, then in-network, then nearest. 'distance' and 'rating'
    // fall back to soonest as the tiebreak.
    docs = [...docs].sort((a, b) => {
      if (sortBy === 'distance') {
        const da = a._distance ?? 9999, db = b._distance ?? 9999
        if (da !== db) return da - db
        return slotMillis(a._nextSlot) - slotMillis(b._nextSlot)
      }
      if (sortBy === 'rating') {
        const ra = a.google_rating ?? 0, rb = b.google_rating ?? 0
        if (rb !== ra) return rb - ra
        return slotMillis(a._nextSlot) - slotMillis(b._nextSlot)
      }
      const sa = slotMillis(a._nextSlot), sb = slotMillis(b._nextSlot)
      if (sa !== sb) return sa - sb
      if (patientHasInsurance) {
        const ia = doctorAcceptsInsurance(a) ? 0 : 1, ib = doctorAcceptsInsurance(b) ? 0 : 1
        if (ia !== ib) return ia - ib
      }
      return (a._distance ?? 9999) - (b._distance ?? 9999)
    })

    return docs
  }, [allDoctorsLive, searchQuery, specialtyFilter, insuranceActive, virtualActive, availableActive, distanceFilter, area, sortBy, patientHasInsurance, patientCarrierId, patientCarrierName, patientIpaId])

  const myDoctorIds = new Set(myDoctors.map(d => d.id))
  // v242.202.0 — TEST 1.18. `favoriteIds` was already fetched here and read only by
  // the Favorites tab, so on the directory list a saved doctor was drawn exactly like
  // an unsaved one. Same flag, same mark, same component as My Doctors — one Set so
  // the card lookup is not a linear scan per row.
  const favoriteSet = useMemo(() => new Set(favoriteIds), [favoriteIds])
  // v230.11.3 — main "Orella Doctors" list shows ALL matching doctors. (My Doctors
  // moved to its own screen in v230.9.0, so subtracting case-linked doctors here
  // only made them silently vanish from the directory.)
  const browseDoctors = filteredDoctors
  const filteredMyDoctors = searchQuery.trim() 
    ? myDoctors.filter(d => `${d.first_name} ${d.last_name} ${(d.specialties || []).join(' ')} ${d.practice_name} ${(d.languages_spoken || []).join(' ')}`.toLowerCase().includes(searchQuery.toLowerCase()))
    : myDoctors

  const favoriteDoctors = useMemo(() => {
    if (!favoriteIds.length) return []
    const favSet = new Set(favoriteIds)
    const all = [...allDoctors, ...myDoctors]
    const seen = new Set()
    return all.filter(d => {
      if (!favSet.has(d.id) || seen.has(d.id)) return false
      seen.add(d.id)
      return true
    })
  }, [allDoctors, myDoctors, favoriteIds])

  const filteredFavorites = searchQuery.trim()
    ? favoriteDoctors.filter(d => `${d.first_name} ${d.last_name} ${(d.specialties || []).join(' ')} ${d.practice_name}`.toLowerCase().includes(searchQuery.toLowerCase()))
    : favoriteDoctors

  const handleDoctorTap = (doc) => {
    navigate(`/doctor/${doc.id}`, { state: { mode: 'browse' } })
  }

  const resetFilters = () => {
    setSpecialtyFilter(null); setDistanceFilter(DEFAULT_MILES); setVirtualActive(false)
    setInsuranceActive(false); setSortBy('soonest')
  }
  const activeFilterCount = (specialtyFilter ? 1 : 0) + (distanceFilter && distanceFilter !== DEFAULT_MILES ? 1 : 0) +
    (virtualActive ? 1 : 0) + (insuranceActive ? 1 : 0) + (sortBy !== 'soonest' ? 1 : 0)

  // ★ s106 (TestFlight b72, Dr. Rami: "No doctors on orella match but other doctors are nearly. Need to
  //   reframe this no results prompt"; his edit of the mockup: "they dont work for orella, they are on
  //   orella"): say what is there. One line on Orella, then how many other doctors are nearby.
  // ★ s123 (board C, copy v1 lines 33–36): what is true here, then Widen (the next distance) and Invite a clinician.
  const noOrellaHere = () => {
    const miles = area ? (distanceFilter || 25) : distanceFilter // s120: the chip's radius, also around a searched area
    return <NoOrellaYet miles={miles} specialty={specialtyFilter} othersCount={orellaOnly ? 0 : dirRows.length} onWiden={(n) => setDistanceFilter(n)} />
  }
  // s123 (board A/D): the PCP from Profile › Insurance (need not be on Orella, his ruling 2026-10-29).
  const pcp = insurance && insurance.pcp && insurance.pcp.name ? insurance.pcp : null
  const seenDoctors = myDoctors
  const officeN = useMemo(() => officeCount(dirRows, groupOffices), [dirRows]) // s124: offices, never the cap
  const otherCount = orellaOnly ? 0 : officeN.offices
  const otherCapped = !orellaOnly && officeN.capped
  const practices = useMemo(() => (searchQuery ? practicesFromRows(dirRows) : []), [searchQuery, dirRows])

  // s123: one set of map props for the in-page map and the full-screen one (board B), so they cannot drift.
  const mapProps = {
    doctors: orellaOnly ? filteredDoctors : [...filteredDoctors, ...dirMapPins(dirRows)],
    loading: dirLoading, loadingLabel: t('common.loading'),
    loadingText: t('doctors.findingInArea', { defaultValue: 'Finding clinicians in this area…' }),
    center: area ? { lat: area.lat, lng: area.lng } : userLocation,
    centerPrecise: area ? false : (userLocation ? userLocation.precise : false),
    youPoint: area ? (userLocation ? { lat: userLocation.lat, lng: userLocation.lng } : null) : undefined,
    youPrecise: !!(userLocation && userLocation.precise),
    onViewportChange: setAreaView,
    recenterKey: mapArea.recenterKey,
    selectedId: selectedMapDoc ? selectedMapDoc.id : (selectedDirNpi ? `npi-${selectedDirNpi}` : null),
    onSelect: (d) => {
      // s123 (board F): a pin for several clinicians at one address opens that office's send sheet.
      if (d && d.__directory && d.__count > 1 && Array.isArray(d.__officeRows)) {
        const offices = groupOffices(d.__officeRows)
        setSelectedMapDoc(null); setSelectedDirNpi(null)
        setPracticeSheet({ name: (offices[0] && offices[0].practice) || '', offices })
        return
      }
      if (d && d.__directory) { setSelectedMapDoc(null); setSelectedDirNpi(d.npi) }
      else { setSelectedDirNpi(null); setSelectedMapDoc(d) }
    },
    orellaOf: (d) => !d.__directory,
    fitRadiusMiles: distanceFilter || 25,
    fitExact: !!distanceFilter,
    mapsKey: MAPS_KEY,
  }
  const pinCard = dirSelected ? (
    <MapPinCard kind="directory" doc={dirSelected} miles={dirSelected.miles} t={t} links
      onSend={() => (otherOffices(dirSelected) > 0 ? setOfficePick(dirSelected) : setStartOffice(dirSelected))}
      onClose={() => setSelectedDirNpi(null)} />
  ) : selectedMapDoc ? (
    <MapPinCard kind="orella" doc={selectedMapDoc} miles={selectedMapDoc._distance} t={t} links
      onClose={() => setSelectedMapDoc(null)}
      onViewProfile={() => handleDoctorTap(selectedMapDoc)} />
  ) : null

  return (
    <div ref={scrollTopRef} className="min-h-full bg-white">
      {/* Special navy header — echoes the hamburger drawer (v242.5.11) */}
      <div className="relative overflow-hidden" style={{ background: '#15354F', borderBottomLeftRadius: 24, borderBottomRightRadius: 24, paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        {/* soft light-circle bokeh */}
        <div className="absolute rounded-full" style={{ top: -30, right: 30, width: 120, height: 120, background: '#3E6E97', opacity: 0.22 }} />
        <div className="absolute rounded-full" style={{ top: 38, right: -24, width: 70, height: 70, background: '#7FB4DC', opacity: 0.14 }} />
        <div className="absolute rounded-full" style={{ top: 10, left: 130, width: 28, height: 28, background: '#A7D9FC', opacity: 0.16 }} />
        <div className="absolute rounded-full" style={{ bottom: 14, right: 90, width: 16, height: 16, background: '#A7D9FC', opacity: 0.20 }} />
        <div className="absolute rounded-full" style={{ bottom: -12, left: 44, width: 46, height: 46, background: '#3E6E97', opacity: 0.16 }} />

        <div className="relative px-5 pt-4 pb-11">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => setMenuOpen(true)} className="p-2 -ml-2" aria-label={t('common.menu', { defaultValue: 'Menu' })}>
                <Menu size={24} style={{ color: '#fff' }} />
              </button>
              <img src={orellaWordmarkWhite} alt="Orella" className="h-6 object-contain" />
            </div>
            <div className="flex items-center gap-2">
              <LanguageButton onClick={() => setLanguageOpen(true)} />
              <NotificationBellButton tone="dark" />
            </div>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <span className="flex items-center justify-center flex-shrink-0" style={{ width: 42, height: 42, borderRadius: 13, background: 'rgba(167,217,252,0.16)', border: '1px solid rgba(167,217,252,0.30)' }}>
              <Activity size={22} style={{ color: '#7FC4FF' }} />
            </span>
            <div className="leading-tight flex-1 min-w-0">
              <p className="text-[23px] font-extrabold" style={{ color: '#fff' }}>{t('doctors.doctors')}</p>
              <p className="text-[12px] font-semibold mt-0.5" style={{ color: '#A7D9FC' }}>{t('doctors.findCareNearYou', { defaultValue: 'Find care near you' })}</p>
            </div>
            {/* s123 (board A): the header's search icon is gone — the search box is always on screen, right below. */}
          </div>
        </div>
      </div>

      <div className="px-4 pt-4 pb-8">

        {/* ★ s123 (board A): ONE card for the name / practice search and the location (copy v1 lines 3–5). The s111
            always-shown name search lives in it (data-testid doctors-name-search). */}
        <DoctorsSearchCard userLocation={userLocation} setShowLoc={setShowLoc} showLocation={!!MAPS_KEY} practiceSearch={nearLive}
          searchQuery={searchQuery} setSearchQuery={setSearchQuery} searchInputRef={searchInputRef} />

        <DoctorFilterChips
          setShowFilters={setShowFilters}
          distanceFilter={distanceFilter} specialtyFilter={specialtyFilter}
          patientHasInsurance={patientHasInsurance}
          insuranceActive={insuranceActive} setInsuranceActive={setInsuranceActive}
          virtualActive={virtualActive} setVirtualActive={setVirtualActive}
          activeFilterCount={activeFilterCount}
          orellaOnly={orellaOnly} setOrellaOnly={setOrellaOnly}
        />

        {/* s123 (board A): My Doctors as two tiles — the PCP and a doctor seen on Orella; See all opens the screen. */}
        <MyDoctorsStrip pcp={pcp} seen={seenDoctors} onSeeAll={() => navigate('/doctors/mine')}
          onPcp={() => navigate('/doctors/mine')} onSeen={(d) => handleDoctorTap(d)} />

        <DoctorFiltersSheet
          showFilters={showFilters} setShowFilters={setShowFilters}
          resetFilters={resetFilters}
          specialtyFilter={specialtyFilter} setSpecialtyFilter={setSpecialtyFilter}
          distanceFilter={distanceFilter} setDistanceFilter={setDistanceFilter}
          virtualActive={virtualActive} setVirtualActive={setVirtualActive}
          sortBy={sortBy} setSortBy={setSortBy}
          patientHasInsurance={patientHasInsurance}
          insuranceActive={insuranceActive} setInsuranceActive={setInsuranceActive}
        />

        {/* s123 (board A): "Near you", the counts and a compact List / Map switch (was a full-width toggle). */}
        <ResultsHeader orellaCount={filteredDoctors.length} otherCount={otherCount} otherCapped={otherCapped} viewMode={viewMode} setViewMode={setViewMode} showToggle={!!MAPS_KEY} />
        {/* s123 (board F): a typed name that matched practices lists them first; Send case opens the office / who sheet. */}
        {searchQuery && !orellaOnly && <PracticeCards practices={practices} onOpen={(p) => setPracticeSheet(p)} />}

        {/* Location sheet — v242.21.0 (FB3): portal to document.body so this fixed overlay
            escapes PageWrapper's framer-motion transform. A transformed ancestor becomes the
            containing block for position:fixed, so the sheet was mispositioned/hidden until a
            navigation reset the transform ("opens only after visiting a doctor"). Mirrors the
            app's BottomSheet / CardScanner portal pattern. */}
        {showLoc && createPortal((
          <div className="fixed inset-0 z-[60] flex flex-col justify-end" style={{ background: 'rgba(15,31,46,0.38)' }} onClick={(e) => { if (e.target === e.currentTarget) setShowLoc(false) }}>
            <div className="bg-white rounded-t-[24px] px-5 pt-3" style={{ boxShadow: '0 -8px 30px rgba(21,53,79,0.10)', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}>
              <div className="w-10 h-1 rounded-full mx-auto mb-3" style={{ background: '#D8E6F0' }} />
              <div className="flex items-center justify-between mb-3">
                <span className="text-[17px] font-extrabold" style={{ color: '#15354F' }}>{t('doctors.searchLocation', { defaultValue: 'Search location' })}</span>
                <button onClick={() => setShowLoc(false)} className="text-[13px] font-extrabold" style={{ color: '#46A8F9' }}>{t('doctors.done', { defaultValue: 'Done' })}</button>
              </div>
              <button onClick={useDeviceLocation} className="flex items-center gap-3 w-full py-3 text-left" style={{ borderBottom: '1px solid #E7EEF4' }}>
                <LocateFixed size={18} style={{ color: '#46A8F9' }} />
                <span className="text-[14px] font-bold" style={{ color: '#46A8F9' }}>{t('doctors.useCurrentLocation', { defaultValue: 'Use my current location' })}</span>
              </button>
              <div className="flex items-center gap-2 rounded-[12px] px-3 py-2.5 my-3" style={{ border: '1px solid #E7EEF4' }}>
                <Search size={15} style={{ color: '#94A3B8', flexShrink: 0 }} />
                <input ref={locInputRef} placeholder={t('doctors.cityZipAddress', { defaultValue: 'City, ZIP, or address' })} className="flex-1 text-[13.5px] outline-none bg-transparent" style={{ color: '#15354F' }} />
              </div>
              {patientZip && (
                <button onClick={useHomeZip} className="flex items-center gap-3 w-full py-3 text-left">
                  <MapPin size={18} style={{ color: '#15354F' }} />
                  <span className="text-[14px] font-bold" style={{ color: '#15354F' }}>{patientZip} · {t('doctors.home', { defaultValue: 'Home' })}</span>
                </button>
              )}
            </div>
          </div>
        ), document.body)}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <OrellaSpinner size={32} className="mb-3" />
            <p className="text-sm" style={{ color: '#64748B' }}>{t('doctors.findingDoctors')}</p>
          </div>
        ) : viewMode === 'map' && MAPS_KEY ? (
          <div>
            <div ref={mapBoxRef} className="rounded-2xl overflow-hidden mb-3 relative" style={{ border: '1px solid #E7EEF4', height: 300 }}>
              <DoctorsMap {...mapProps} />
              {/* PROD-1184: "My location" whenever the map is away from the patient's point, also with a pin card open. */}
              <MapAreaButtons mapArea={mapArea} cardOpen={!!(dirSelected || selectedMapDoc)} t={t} />
              {/* s123 (board A → B): open the same map full screen. */}
              {!mapFull && <MapExpandButton onClick={() => setMapFull(true)} />}
              {!mapFull && pinCard}
            </div>
            <MapLegend />
            {filteredDoctors.filter(d => d.practice_lat == null || d.practice_lng == null).length > 0 && (
              <p className="text-[11.5px] text-center mb-3" style={{ color: '#64748B' }}>
                {filteredDoctors.filter(d => d.practice_lat == null || d.practice_lng == null).length} {t('doctors.notOnMap', { defaultValue: 'not on the map yet (location pending)' })}
              </p>
            )}
            {/* Full card list, synced to the map: tapping a pin highlights its card (v242.5.11) */}
            {browseDoctors.length === 0 ? (<>{noOrellaHere()}<PcpRow pcp={pcp} onSend={setStartOffice} /></>) : (
              <div className="space-y-2">
                {browseDoctors.map(doc => (
                  <DoctorCard key={doc.id} doc={doc} isMyDoctor={myDoctorIds.has(doc.id)} isFavorite={favoriteSet.has(doc.id)} insurance={insurance} isSelected={selectedMapDoc ? selectedMapDoc.id === doc.id : false} onTap={() => handleDoctorTap(doc)} />
                ))}
              </div>
            )}
            <div className="mt-5 pt-4 border-t border-gray-100" data-testid="doctors-directory-section" style={orellaOnly ? { display: 'none' } : undefined}>
              <DirectoryNearby variant="section" sectionTitle={officeN.capped ? t('doctors.manyOffices', { defaultValue: 'Many offices nearby' }) : t('doctors.officesWithin', { defaultValue: '{{count}} offices within {{miles}} mi', count: officeN.offices, miles: distanceFilter || 25 })} sectionNote={t('doctors.nearestFirst', { defaultValue: 'Nearest first' })} zip={area && area.zip ? area.zip : dirZip} radiusMiles={distanceFilter || 25} q={searchQuery}
                origin={distanceOrigin}
                point={area ? { lat: area.lat, lng: area.lng } : distanceOrigin} onSource={(src) => setNearLive(src === 'near')}
                taxonomy={dirTaxonomy} onRows={setDirRows} onLoading={setDirLoading} selectedNpi={selectedDirNpi} t={t}
                onPick={(r) => { setSelectedMapDoc(null); setSelectedDirNpi(r.npi); try { mapBoxRef.current && mapBoxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }) } catch (e) { /* older WebKit */ } }} />
            </div>
          </div>
        ) : (
          <>
            {/* Browse All */}
            <div>
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#64748B' }}>
                  {searchQuery || specialtyFilter || insuranceActive || virtualActive || availableActive || distanceFilter ? 'Results' : 'Orella doctors'}
                </p>
                <span className="text-xs text-orella-500 font-medium">
                  {browseDoctors.length} doctor{browseDoctors.length !== 1 ? 's' : ''}
                </span>
              </div>
              
              {browseDoctors.length === 0 ? (
                searchQuery ? (
                <div className="text-center py-8">
                  <Search className="w-8 h-8 mx-auto mb-3" style={{ color: '#CBD5E1' }} />
                  <p className="text-sm" style={{ color: '#64748B' }}>{t('doctors.noDoctorsForSearch', { query: searchQuery })}</p>
                  <button onClick={() => { setSearchQuery(''); setSpecialtyFilter(null); setInsuranceActive(false); setVirtualActive(false); setAvailableActive(false); setDistanceFilter(DEFAULT_MILES) }} className="text-xs text-orella-500 font-medium mt-2">
                    {t('doctors.clearFilters', 'Clear filters')}
                  </button>
                </div>
                ) : (<>{noOrellaHere()}<PcpRow pcp={pcp} onSend={setStartOffice} /></>)
              ) : (
                <div className="space-y-2">
                  {browseDoctors.map(doc => (
                    <DoctorCard key={doc.id} doc={doc} isMyDoctor={myDoctorIds.has(doc.id)} isFavorite={favoriteSet.has(doc.id)} insurance={insurance} onTap={() => handleDoctorTap(doc)} />
                  ))}
                </div>
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-gray-100" data-testid="doctors-directory-section" style={orellaOnly ? { display: 'none' } : undefined}>
              <DirectoryNearby variant="section" sectionTitle={officeN.capped ? t('doctors.manyOffices', { defaultValue: 'Many offices nearby' }) : t('doctors.officesWithin', { defaultValue: '{{count}} offices within {{miles}} mi', count: officeN.offices, miles: distanceFilter || 25 })} sectionNote={t('doctors.nearestFirst', { defaultValue: 'Nearest first' })} zip={area && area.zip ? area.zip : dirZip} radiusMiles={distanceFilter || 25} q={searchQuery}
                origin={distanceOrigin}
                point={area ? { lat: area.lat, lng: area.lng } : distanceOrigin} onSource={(src) => setNearLive(src === 'near')}
                taxonomy={dirTaxonomy} onRows={setDirRows} onLoading={setDirLoading} selectedNpi={selectedDirNpi} t={t}
                onPick={(r) => { setSelectedMapDoc(null); setSelectedDirNpi(r.npi); if (MAPS_KEY) setViewMode('map') }} />
            </div>
          </>
        )}

      </div>

      {/* ★ s123 (board B): the map full screen — the same map props, the loader pill while results refresh (they refresh
          by themselves after the map stops, useMapArea), and the results in a sheet at the bottom. */}
      {mapFull && MAPS_KEY && createPortal((
        <div className="fixed inset-0 z-[55] bg-white" data-testid="doctors-map-full">
          <div className="absolute inset-0">
            <DoctorsMap {...mapProps} />
          </div>
          <div className="absolute left-3 right-3 flex items-center gap-2" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)', zIndex: 6 }}>
            <button onClick={() => setMapFull(false)} aria-label={t('doctors.backToList', { defaultValue: 'Back to list' })}
              className="w-11 h-11 rounded-[14px] bg-white grid place-items-center flex-shrink-0" style={{ color: '#15354F', boxShadow: '0 2px 10px rgba(21,53,79,0.16)' }}>
              <ChevronDown size={20} style={{ transform: 'rotate(90deg)' }} />
            </button>
          </div>
          <div data-map-btns="" className="absolute left-0 right-0" style={{ zIndex: 6, top: 64, bottom: '42vh', pointerEvents: 'none' }}>
            <style>{'[data-map-btns] button { pointer-events: auto; }'}</style>
            <MapAreaButtons mapArea={mapArea} cardOpen={false} t={t} />
          </div>
          {pinCard ? (
            <div className="absolute left-0 right-0 bottom-0" style={{ zIndex: 7, paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>{pinCard}</div>
          ) : (
            <section aria-label={t('doctors.inThisArea', { defaultValue: 'In this area' })} className="absolute left-0 right-0 bottom-0 bg-white rounded-t-[24px] px-4 pt-2.5 overflow-y-auto"
              style={{ zIndex: 7, maxHeight: '42vh', boxShadow: '0 -6px 24px rgba(21,53,79,0.14)', paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
              <div className="w-10 h-1.5 rounded-full mx-auto mb-2.5" style={{ background: '#D8E6F0' }} />
              <div className="flex items-baseline justify-between mb-2.5">
                <h2 className="text-[16px] font-extrabold" style={{ color: '#15354F' }}>{t('doctors.inThisArea', { defaultValue: 'In this area' })}</h2>
                <span className="text-[12px]" style={{ color: '#64748B' }}>
                  {otherCapped
                    ? t('doctors.resultsCountMany', { defaultValue: '{{orella}} on Orella · many offices nearby', orella: filteredDoctors.length })
                    : otherCount === 1
                    ? t('doctors.resultsCountOneOther', { defaultValue: '{{orella}} on Orella · 1 other office', orella: filteredDoctors.length })
                    : t('doctors.resultsCount', { defaultValue: '{{orella}} on Orella · {{other}} other offices', orella: filteredDoctors.length, other: otherCount })}
                </span>
              </div>
              <div className="space-y-2">
                {browseDoctors.map(doc => (
                  <DoctorCard key={doc.id} doc={doc} isMyDoctor={myDoctorIds.has(doc.id)} isFavorite={favoriteSet.has(doc.id)} insurance={insurance} onTap={() => handleDoctorTap(doc)} />
                ))}
                {!orellaOnly && dirRows.slice(0, 20).map((r) => (
                  <button key={`npi-${r.npi}`} onClick={() => { setSelectedMapDoc(null); setSelectedDirNpi(r.npi) }}
                    className="w-full text-left bg-white rounded-2xl px-3.5 py-3 flex items-center gap-3" style={{ border: '1px solid #E3ECF3' }}>
                    <span className="flex-1 min-w-0">
                      <span className="block text-[14px] font-bold truncate" style={{ color: '#15354F' }}>{r.name}{r.credential ? `, ${r.credential}` : ''}</span>
                      <span className="block text-[12px] truncate" style={{ color: '#475569' }}>{[r.specialty, typeof r.miles === 'number' ? `${r.miles.toFixed(1)} mi` : null].filter(Boolean).join(' · ')}</span>
                    </span>
                    <ChevronRight size={16} style={{ color: '#C8D6E2' }} />
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      ), document.body)}

      {practiceSheet && <PracticeSendSheet practice={practiceSheet} t={t} onClose={() => setPracticeSheet(null)}
        onChoose={(target) => { setPracticeSheet(null); setStartOffice(target) }} />}

      {/* s106: "Send my case to this office" — the case starts with this office saved for the share hub. */}
      {startOffice && <StartCaseSheet office={startOffice} onClose={() => setStartOffice(null)} />}
      {officePick && <OfficePickerSheet provider={officePick} t={t} zip={pickerZip}
        origin={pickerOrigin}
        onClose={() => setOfficePick(null)} onChoose={(p) => { setOfficePick(null); setStartOffice(p) }} />}

      {/* Global overlays — same components as the app top bar (v242.5.11) */}
      <HamburgerMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} />
      <LanguageSelector isOpen={languageOpen} onClose={() => setLanguageOpen(false)} />
    </div>
  )
}
